import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import ResponderProtectedRoute from "../components/ResponderProtectedRoute";
import MainLayout from "../components/layout/MainLayout";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Lazy load components
const Login = lazy(() => import("@/pages/auth/login/Login"));
const Dashboard = lazy(() => import("@/pages/dashboard/Dashboard"));
const IncidentMap = lazy(() => import("@/pages/map/Map"));
const Teams = lazy(() => import("@/pages/teams/Teams"));
const Users = lazy(() => import("@/pages/users/Users"));
const FirstResponders = lazy(() => import("@/pages/first-responders/FirstResponders"));
const AccountSettings = lazy(() => import("@/pages/account/AccountSettings"));
const ErrorPageResponder = lazy(() => import("@/pages/responder/ErrorPageResponder"));
const HomeResponder = lazy(() => import("@/pages/responder/HomeResponder"));
const Location = lazy(() => import("@/pages/location/Location"));

function AppRoutes() {
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get("id");

  return (
    <Suspense fallback={<div className="text-center p-5 h-[100vh] flex items-center justify-center">  <LoadingSpinner size="large" color="rgb(5 83 156)" /></div>}>
      {assignmentId ? (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/responder" element={<ResponderProtectedRoute><HomeResponder /></ResponderProtectedRoute>} />
          <Route path="/error" element={<ErrorPageResponder />} />
          <Route path="*" element={<Navigate to={`/login?id=${assignmentId}`} replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="map"
              element={
                <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                  <IncidentMap />
                </ProtectedRoute>
              }
            />
            <Route
              path="teams"
              element={
                <ProtectedRoute allowedRoles={["Administrator"]}>
                  <Teams />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute allowedRoles={["Administrator"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="first-responders"
              element={
                <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                  <FirstResponders />
                </ProtectedRoute>
              }
            />
            <Route
              path="location"
              element={
                <ProtectedRoute allowedRoles={["Administrator"]}>
                  <Location />
                </ProtectedRoute>
              }
            />
            <Route
              path="account"
              element={
                <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route path="alerts">
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                    <div className="relative h-full">
                      <IncidentMap />
                    </div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="chat/:alertId"
                element={
                  <ProtectedRoute allowedRoles={["Administrator", "Operator"]}>
                    <div className="relative h-full">
                      <IncidentMap />
                    </div>
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/alerts" replace />} />
        </Routes>
      )}
    </Suspense>
  );
}

export default AppRoutes;
